import { Component, OnInit, Input, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ConfigConstant, RolesConstants } from '@shared/constants';
import { Subscription, Observable } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { CustomerServiceType, FormAction, LookupType, UserType } from '@shared-features/enums/shared-enums.enum';
import { TabGroup } from '@shared-features/enums/tab.enum';
import { FilterService, StatusFilterOption } from '../../../filter/services/filter.service';
import { Fleet } from '@features/fleets/models/fleet.model';
import { BaseComponent } from '@shared-features/components/base-component/base.component';
import { takeUntil } from 'rxjs/operators';
import { UserApi } from '@shared-features/services';
import { UserRoleService } from '@shared/services';
import { MatOption } from '@angular/material/core';
import { Customer } from '@features/customers/models/customer.model';
import { User } from '@features/users/models/user.model';
import { Receiver } from '@features/receivers/models/receiver.model';
import { modelToLookup } from '@shared/utils/model-to-lookup.util';
import { ALL_OPTION_VALUE } from '@shared/components/forms';
import { Lookup } from '@shared-features/models/Lookup.model';
import { parseQueryParam } from '@shared/utils/parse-query-param';
import { ShipmentDirection, ShipmentRequestsMultiplicationType, ShipmentStatus, TrackingType } from '@shared-features/enums/shipments.enum';
import { BayanTypesEnum } from '@features/shipments/enums/bayen.enum';
import { CustomerType } from '@shared/enums/global.enum';
import { FiltersBase } from '@shared/services/filters-base.service';
@Component({
  selector: 'app-shipments-filter',
  templateUrl: './shipments-filter.component.html',
  styleUrls: ['./shipments-filter.component.scss'],
})
export class ShipmentsFilterComponent extends BaseComponent implements OnInit {
  @Input() action: Observable<void>;
  tabIndex;
  userId: number;
  form: FormGroup;
  GlobalConstants = ConfigConstant;

  filterActionSubscription: Subscription;

  params: any = null;
  receivers: Receiver[] = [];
  customers: Customer[] = [];
  creators: User[] = [];
  fleets: Fleet[] = [];
  isCustomerTypeSAAS: boolean;
  isOrganizationUser: boolean;
  isFleetUser: boolean;
  isAllCustomersSelected = true;
  protected LookupType = LookupType;

  readonly ALL = ConfigConstant.ALL;
  readonly enumFilterTranslationKey = 'shipments';
  readonly shipmentCustomerTypes = { [ConfigConstant.ALL]: ConfigConstant.ALL, ...CustomerType };
  readonly shipmentRequestsMultiplicationTypes = { [ConfigConstant.ALL]: ConfigConstant.ALL, ...ShipmentRequestsMultiplicationType };
  readonly directions = { [ConfigConstant.ALL]: ConfigConstant.ALL, ...ShipmentDirection };
  readonly bayanTypes = BayanTypesEnum;
  readonly trackingTypes = TrackingType;
  readonly customerTypesCustomLabels = { Service: 'Ops' };
  readonly trackingTypesCustomLabels = { Empty: 'NoTracking' };
  readonly statusNone = ShipmentStatus.None;
  statusOptions: StatusFilterOption[] = [];

  @ViewChild('allSelected') allSelected: MatOption;

  constructor(
    private filterService: FilterService,
    private activatedRoute: ActivatedRoute,
    private formBuilder: FormBuilder,
    private userAPI: UserApi,
    private userRoleService: UserRoleService
  ) {
    super();
  }

  ngOnInit(): void {
    this.userId = this.userAPI.currentUser$.value.parentId;
    this.isCustomerTypeSAAS = this.userAPI.currentUser$.value.customerType === CustomerServiceType.SAAS;
    (this.isOrganizationUser = this.userRoleService.isUserHasRoles(RolesConstants.SHIPMENT_FILTRATION_ORGANIZATION_VIEW)),
      (this.isFleetUser = this.userAPI.currentUser$.value.roles[0].userType === UserType.FLEET);
    this.subscribeToQueryParams();
    this.subscribeToFilterAction();
    this.subscribeToStatusOptions();
    this.initForm();
  }

  subscribeToStatusOptions(): void {
    this.filterService.shipmentStatusOptions$.pipe(takeUntil(this.destroy$)).subscribe((options) => {
      this.statusOptions = options || [];
    });
  }

  getStatusLabel(value: string): string {
    return this.statusOptions.find((option) => option.value === value)?.label ?? '';
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.filterActionSubscription.unsubscribe();
  }

  subscribeToQueryParams(): void {
    this.activatedRoute.queryParams.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      if (params && Object.keys(params).length !== 0) {
        this.params = params;
      }
    });
  }

  subscribeToFilterAction() {
    this.filterActionSubscription = this.action.subscribe((action: any) => {
      switch (action) {
        case FormAction.Reset:
          this.resetForm();
          break;

        case FormAction.Cancel:
          this.cancelChanges();
          break;

        case FormAction.Submit:
          this.submitForm();
          break;
      }
    });
  }

  generateAllLookup(string) {
    return [
      Object.assign(new Lookup(), {
        value: 'All ' + string,
        id: ALL_OPTION_VALUE,
      }),
    ];
  }

  generateSelectedOptions(data: any, dataKey: string, allLookupOption: any, key: string): any {
    const allLookup = this.generateAllLookup(allLookupOption);
    const isArray = Array.isArray(data[dataKey]);
    const includesAll = isArray ? data[dataKey]?.includes(ALL_OPTION_VALUE) : false;
    const isAllFirst = isArray ? data[dataKey]?.[0] === 'All' : false;
    const isEmpty = isArray ? data[dataKey]?.length === 0 : false;

    if (includesAll || isAllFirst || isEmpty) {
      return allLookup;
    } else {
      return (data?.[dataKey] || []).map((id: any, i: number) => {
        const obj: any = { id: id, name: data[key][i] };
        return modelToLookup(obj, 'id', 'name');
      });
    }
  }

  getFilterValuesBasedOnURLParams() {
    const params = parseQueryParam(
      this.params,
      ['customerId', 'customerName', 'fleetId', 'fleetName', 'receiverId', 'receiverName', 'creator', 'creatorName', 'fromCityId', 'fromCityName', 'toCityId', 'toCityName']
    );

    return {
      customerId: this.generateSelectedOptions(params, 'customerId', 'Customer', 'customerName'),
      receiverId: this.generateSelectedOptions(params, 'receiverId', 'Receiver', 'receiverName'),
      fleetId: this.generateSelectedOptions(params, 'fleetId', 'Fleet', 'fleetName'),
      creator: this.generateSelectedOptions(params, 'creator', 'Creator', 'creatorName'),
      fromCityId: this.generateSelectedOptions(params, 'fromCityId', 'City', 'fromCityName'),
      toCityId: this.generateSelectedOptions(params, 'toCityId', 'City', 'toCityName'),
    };
  }

  getEnumFilterValue(key: string): any {
    const raw = this.params?.[key];
    return raw === undefined || raw === null || raw === ConfigConstant.ALL ? ConfigConstant.ALL : FiltersBase.parseValue(raw);
  }

  initForm() {
    const filterValues = this.getFilterValuesBasedOnURLParams();
    this.form = this.formBuilder.group({
      customerId: [filterValues?.customerId],
      receiverId: [filterValues?.receiverId],
      fleetId: [filterValues?.fleetId],
      creator: [filterValues?.creator],
      fromCityId: [filterValues?.fromCityId],
      toCityId: [filterValues?.toCityId],
      customerServiceType: [this.getEnumFilterValue('customerServiceType')],
      isBayanVerified: [this.getEnumFilterValue('isBayanVerified')],
      shipmentRequestFilterCount: [this.getEnumFilterValue('shipmentRequestFilterCount')],
      trackingTypeFilter: [this.getEnumFilterValue('trackingTypeFilter')],
      direction: [this.getEnumFilterValue('direction')],
      status: [this.params?.status ?? this.statusNone],
    });
  }

  cancelChanges() {
    if (this.params && Object.keys(this.params).length !== 0) {
      const filterValues = this.getFilterValuesBasedOnURLParams();
      this.form?.setValue({
        customerId: filterValues.customerId,
        receiverId: filterValues.receiverId,
        fleetId: filterValues.fleetId,
        creator: filterValues.creator,
        fromCityId: filterValues.fromCityId,
        toCityId: filterValues.toCityId,
        customerServiceType: this.getEnumFilterValue('customerServiceType'),
        isBayanVerified: this.getEnumFilterValue('isBayanVerified'),
        shipmentRequestFilterCount: this.getEnumFilterValue('shipmentRequestFilterCount'),
        trackingTypeFilter: this.getEnumFilterValue('trackingTypeFilter'),
        direction: this.getEnumFilterValue('direction'),
        status: this.params?.status ?? this.statusNone,
      });
    } else {
      this.resetForm();
    }
  }

  resetForm() {
    this.form?.setValue({
      customerId: this.generateAllLookup('Customer'),
      receiverId: this.generateAllLookup('Receiver'),
      fleetId: this.generateAllLookup('Fleet'),
      creator: this.generateAllLookup('Creator'),
      fromCityId: this.generateAllLookup('City'),
      toCityId: this.generateAllLookup('City'),
      customerServiceType: ConfigConstant.ALL,
      isBayanVerified: ConfigConstant.ALL,
      shipmentRequestFilterCount: ConfigConstant.ALL,
      trackingTypeFilter: ConfigConstant.ALL,
      direction: ConfigConstant.ALL,
      status: this.statusNone,
    });
    this.submitForm();
  }

  submitForm() {
    const formValue = this.form?.value;
    const { customerId, receiverId, fleetId, creator, fromCityId, toCityId } = formValue;
    const { customerServiceType, isBayanVerified, shipmentRequestFilterCount, trackingTypeFilter, direction, status } = formValue;

    const filteredValues = {
      customerServiceType,
      isBayanVerified,
      shipmentRequestFilterCount,
      trackingTypeFilter,
      direction,
      status,
      fromCityId: this.getParamValue(true, fromCityId, 'id'),
      fromCityName: this.getParamValue(true, fromCityId, 'name'),
      toCityId: this.getParamValue(true, toCityId, 'id'),
      toCityName: this.getParamValue(true, toCityId, 'name'),
      offset: ConfigConstant.INIT_PAGE_OFFSET,

      receiverId: this.getParamValue(!this.isOrganizationUser && !this.isFleetUser, receiverId, 'id'),
      receiverName: this.getParamValue(!this.isOrganizationUser && !this.isFleetUser, receiverId, 'name'),

      fleetId: this.getParamValue(!this.isOrganizationUser && !this.isFleetUser, fleetId, 'id'),
      fleetName: this.getParamValue(!this.isOrganizationUser && !this.isFleetUser, fleetId, 'name'),

      creator: this.getParamValue(!this.isOrganizationUser && !this.isFleetUser, creator, 'id'),
      creatorName: this.getParamValue(!this.isOrganizationUser && !this.isFleetUser, creator, 'name'),

      customerId: this.getParamValue(this.isOrganizationUser || this.isFleetUser, customerId, 'id'),
      customerName: this.getParamValue(this.isOrganizationUser || this.isFleetUser, customerId, 'name'),

      tabIndex: this.tabIndex,
      tabGroup: TabGroup.Shipments,
    };

    this.filterService.navigateToTargetPageWithFilter(filteredValues);
  }

  getParamValue(condition, array, result) {
    const arrayIds = array?.map((el) => el?.id).filter(Boolean).length ? array?.map((el) => el.id) : [];
    const arrayNames = array?.map((el) => el?.value).filter(Boolean).length ? array?.map((el) => el.value) : [];

    if (condition) {
      return result === 'id' ? arrayIds : arrayNames;
    } else {
      return null;
    }
  }
}
